import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import * as api from "../../api/statoLavori";
import { getErrorMessage } from "../../api/client";
import { DispositivoDiSede } from "../../api/clients";
import { SERVIZI_PRECREDIT, SERVIZIO_PRECREDIT_LABELS, TipoServizioPrecredit } from "../../api/devices";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { useToast } from "../../context/ToastContext";
import { dispositivoAssociabile, nomeCassaforte, nomeSede } from "./presentazione";
import { etichettaMatricola } from "../../utils/matricola";

type Scelta = "nuova" | "dispositivo" | "condivisa";

/**
 * «Avvia pratica» dalla scheda della sede (issue #211). Una pratica è di un
 * cliente su una cassaforte, e la cassaforte è una di tre:
 *   - una nuova, prima che esista il dispositivo, con un'etichetta facoltativa;
 *   - un dispositivo con una relazione aperta in questa sede;
 *   - una cassaforte già in lavorazione per un altro cliente (condivisa).
 * Il servizio e «installare entro» sono facoltativi: senza servizio la pratica
 * prende quello della relazione del dispositivo, e se manca anche quello è «da
 * verificare». Interlocutore e cartella si aggiungono dalla scheda della
 * pratica, dove ci sono anche i suggerimenti.
 *
 * «Sede già attiva» (PR E1) segna subito «non necessario» i passi preliminari
 * aperti; si propone spuntata quando il cliente ha già un'attivazione fatta
 * sul dispositivo scelto.
 */
export function AvviaPratica({
  sedeId,
  clienteId,
  dispositivi,
  onClose,
}: {
  sedeId: string;
  /** Il cliente della sede: le casseforti dove ha già una pratica non si offrono da condividere. */
  clienteId: string;
  dispositivi: DispositivoDiSede[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  // Solo i dispositivi di tipo Cassaforte o non ancora classificati: gli altri il
  // backend li rifiuta (400).
  const associabili = dispositivi.filter((d) => dispositivoAssociabile(d.tipo_dispositivo));
  const esclusi = dispositivi.length - associabili.length;
  const [scelta, setScelta] = useState<Scelta>(associabili.length > 0 ? "dispositivo" : "nuova");
  const [etichetta, setEtichetta] = useState("");
  const [deviceId, setDeviceId] = useState(associabili.length === 1 ? associabili[0].id : "");
  const [cassaforteId, setCassaforteId] = useState("");
  const [servizio, setServizio] = useState<TipoServizioPrecredit | "">("");
  const [installareEntro, setInstallareEntro] = useState("");
  /** NULL: si segue la proposta del dispositivo scelto; altrimenti la scelta fatta a mano. */
  const [sedeAttivaScelta, setSedeAttivaScelta] = useState<boolean | null>(null);
  const [inLavorazione, setInLavorazione] = useState<api.RigaPratica[] | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<{ testo: string; praticaId: string | null } | null>(null);

  // Le casseforti già in lavorazione si caricano solo se servono.
  useEffect(() => {
    if (scelta !== "condivisa" || inLavorazione !== null) return;
    api
      .listPratiche({ stato: "in_corso" })
      .then((d) => setInLavorazione(d.righe))
      .catch((err) => setErrore({ testo: getErrorMessage(err, "Impossibile caricare le casseforti in lavorazione."), praticaId: null }));
  }, [scelta, inLavorazione]);

  // Una riga per cassaforte, anche se ha più pratiche, e solo quelle dove il
  // cliente non ne ha già una: lì il backend risponderebbe col doppione (409).
  const giaSue = new Set((inLavorazione ?? []).filter((r) => r.cliente.id === clienteId).map((r) => r.cassaforte.id));
  const casseforti = (inLavorazione ?? []).filter(
    (r, i, tutte) => !giaSue.has(r.cassaforte.id) && tutte.findIndex((x) => x.cassaforte.id === r.cassaforte.id) === i
  );
  const scegli = (s: Scelta) => {
    setScelta(s);
    setSedeAttivaScelta(null);
    setErrore(null);
  };
  const dispositivo = scelta === "dispositivo" ? (associabili.find((d) => d.id === deviceId) ?? null) : null;
  const sedeAttiva = sedeAttivaScelta ?? dispositivo?.attivazione_effettuata ?? false;

  const incompleta = (scelta === "dispositivo" && !deviceId) || (scelta === "condivisa" && !cassaforteId);

  async function invia(e: FormEvent) {
    e.preventDefault();
    if (incompleta || inCorso) return;
    setInCorso(true);
    setErrore(null);
    try {
      const pratica = await api.avviaPratica({
        sede_id: sedeId,
        ...(scelta === "nuova" ? { etichetta: etichetta.trim() || null } : {}),
        ...(scelta === "dispositivo" ? { device_id: deviceId } : {}),
        ...(scelta === "condivisa" ? { lavoro_cassaforte_id: cassaforteId } : {}),
        tipo_servizio_precredit: servizio || null,
        installare_entro: installareEntro || null,
        sede_gia_attiva: sedeAttiva,
      });
      showToast("Pratica avviata.", "success");
      navigate(`/stato-lavori/${pratica.id}`);
    } catch (err) {
      // Il doppione (il cliente ha già una pratica su questa cassaforte) porta
      // l'id della pratica esistente: la si offre invece di lasciare un vicolo cieco.
      const praticaId =
        axios.isAxiosError(err) && err.response?.status === 409 ? ((err.response.data as { pratica_id?: string })?.pratica_id ?? null) : null;
      setErrore({ testo: getErrorMessage(err, "Impossibile avviare la pratica."), praticaId });
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Avvia pratica"
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={inCorso}>
            Annulla
          </Button>
          <Button type="submit" form="avvia-pratica" loading={inCorso} disabled={incompleta}>
            Avvia pratica
          </Button>
        </>
      }
    >
      <form id="avvia-pratica" onSubmit={invia} className="flex flex-col gap-4">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium text-ink">Cassaforte</legend>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="cassaforte" checked={scelta === "dispositivo"} disabled={associabili.length === 0} onChange={() => scegli("dispositivo")} />
            Un dispositivo presso questa sede
          </label>
          {scelta === "dispositivo" && (
            <select
              aria-label="Dispositivo"
              value={deviceId}
              onChange={(e) => {
                setDeviceId(e.target.value);
                setSedeAttivaScelta(null);
              }}
              className="ml-6 h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            >
              <option value="">Scegli il dispositivo</option>
              {associabili.map((d) => (
                <option key={d.id} value={d.id}>
                  {etichettaMatricola(d.matricola)}
                </option>
              ))}
            </select>
          )}
          {associabili.length === 0 && (
            <p className="ml-6 text-xs text-ink/55">
              {esclusi > 0 ? "Nessuna cassaforte censita presso questa sede." : "Nessun dispositivo censito presso questa sede."}
            </p>
          )}
          {esclusi > 0 && (
            <p className="ml-6 text-xs text-ink/55">
              {esclusi === 1
                ? "Un dispositivo di altro tipo (rendiresto, TVCC…) non si propone: non è una cassaforte."
                : `${esclusi} dispositivi di altro tipo (rendiresto, TVCC…) non si propongono: non sono casseforti.`}
            </p>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="cassaforte" checked={scelta === "nuova"} onChange={() => scegli("nuova")} />
            Una cassaforte nuova, non ancora installata
          </label>
          {scelta === "nuova" && (
            <div className="ml-6">
              <Input
                aria-label="Etichetta della cassaforte"
                maxLength={100}
                value={etichetta}
                onChange={(e) => setEtichetta(e.target.value)}
                placeholder="Etichetta facoltativa, es. Back-office"
              />
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="cassaforte" checked={scelta === "condivisa"} onChange={() => scegli("condivisa")} />
            Una cassaforte già in lavorazione per un altro cliente
          </label>
          {scelta === "condivisa" && (
            <select
              aria-label="Cassaforte in lavorazione"
              value={cassaforteId}
              disabled={inLavorazione === null}
              onChange={(e) => setCassaforteId(e.target.value)}
              className="ml-6 h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            >
              <option value="">
                {inLavorazione === null ? "Caricamento…" : casseforti.length === 0 ? "Nessuna cassaforte da condividere" : "Scegli la cassaforte"}
              </option>
              {casseforti.map((r) => (
                <option key={r.cassaforte.id} value={r.cassaforte.id}>
                  {nomeCassaforte(r.cassaforte)} — {r.cliente.nome}, {nomeSede(r.sede)}
                </option>
              ))}
            </select>
          )}
        </fieldset>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="avvia-servizio" className="text-sm font-medium text-ink">
            Servizio
          </label>
          <select
            id="avvia-servizio"
            value={servizio}
            onChange={(e) => setServizio(e.target.value as TipoServizioPrecredit | "")}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          >
            {/* Vuoto = la pratica non dichiara un servizio: vale quello della relazione, se c'è. */}
            <option value="">
              {dispositivo?.servizio_precredit
                ? `Quello del dispositivo: ${SERVIZIO_PRECREDIT_LABELS[dispositivo.servizio_precredit]}`
                : "Non indicato (da verificare)"}
            </option>
            {SERVIZI_PRECREDIT.map((s) => (
              <option key={s} value={s}>
                {SERVIZIO_PRECREDIT_LABELS[s]}
              </option>
            ))}
          </select>
        </div>

        <Input type="date" label="Installare entro (facoltativo)" value={installareEntro} onChange={(e) => setInstallareEntro(e.target.value)} />

        <label className="flex items-start gap-2.5 text-sm">
          <input type="checkbox" className="mt-0.5" checked={sedeAttiva} onChange={(e) => setSedeAttivaScelta(e.target.checked)} />
          <span>
            <span className="font-medium text-ink">Sede già attiva</span>
            <span className="mt-0.5 block text-xs text-ink/60">
              I passi preliminari ancora aperti (1, 2, 5, A, 6 e 8) si segnano subito «non necessario»; ognuno si riapre dalla pratica.
              {dispositivo?.attivazione_effettuata && " Proposta perché il cliente ha già un'attivazione fatta su questo dispositivo."}
            </span>
          </span>
        </label>

        {errore && (
          <p role="alert" className="rounded-lg bg-[#ef4444]/10 px-3 py-2 text-sm text-[#b91c1c]">
            {errore.testo}
            {errore.praticaId && (
              <>
                {" "}
                <Link to={`/stato-lavori/${errore.praticaId}`} className="font-semibold underline">
                  Apri la pratica esistente
                </Link>
              </>
            )}
          </p>
        )}
      </form>
    </Modal>
  );
}
